<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from 'reka-ui'
import SourceCheckbox from '~/components/ui/SourceCheckbox.vue'
import SourceInput from '~/components/ui/SourceInput.vue'
import type { SourceTableFilterOption } from '~/types/sourceTable'
import { useOverlayPopperZ } from '~/composables/ui/useOverlayStack'

const overlayPopperZ = useOverlayPopperZ()

const props = withDefaults(
  defineProps<{
    columnKey: string
    columnLabel: string
    options: SourceTableFilterOption[]
    modelValue?: string[]
  }>(),
  {
    modelValue: () => [],
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', values: string[]): void
}>()

const open = ref(false)
const searchQuery = ref('')
const draftValues = ref<string[]>([...props.modelValue])

const hasActiveFilter = computed(() => props.modelValue.length > 0)

const filteredOptions = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase('fr')
  if (!query) return props.options
  return props.options.filter((option) => option.label.toLocaleLowerCase('fr').includes(query))
})

watch(open, (isOpen) => {
  if (!isOpen) return
  draftValues.value = [...props.modelValue]
  searchQuery.value = ''
})

function isChecked(value: string): boolean {
  return draftValues.value.includes(value)
}

function toggleValue(value: string, checked: boolean): void {
  if (checked) {
    if (!draftValues.value.includes(value)) {
      draftValues.value = [...draftValues.value, value]
    }
    return
  }
  draftValues.value = draftValues.value.filter((entry) => entry !== value)
}

function onReset(): void {
  draftValues.value = []
}

function onApply(): void {
  emit('update:modelValue', [...draftValues.value])
  open.value = false
}

function formatCount(count: number): string {
  return count > 99 ? '99+' : String(count)
}
</script>

<template>
  <PopoverRoot v-model:open="open">
    <PopoverTrigger
      class="source-table-filter__trigger"
      type="button"
      :class="{ 'is-active': hasActiveFilter }"
      :aria-label="`Filtrer la colonne ${columnLabel}`"
      :aria-expanded="open"
      @click.stop
    >
      <span class="material-symbols-rounded" aria-hidden="true">filter_alt</span>
    </PopoverTrigger>

    <PopoverPortal>
      <PopoverContent
        class="source-table-filter__menu"
        side="bottom"
        :align="'start'"
        :side-offset="8"
        :style="{ zIndex: overlayPopperZ }"
        @click.stop
        @keydown.escape.stop="open = false"
      >
        <div class="source-table-filter__search">
          <div class="source-table-filter__search-input">
            <SourceInput
              :label="`Rechercher dans ${columnLabel}`"
              icon="search"
              type="search"
              size="small"
              placeholder="Rechercher…"
              :model-value="searchQuery"
              @update:model-value="(value) => (searchQuery = value)"
            />
          </div>
        </div>

        <div
          class="source-table-filter__options"
          role="group"
          :aria-label="`Options de filtre ${columnLabel}`"
        >
          <p v-if="filteredOptions.length === 0" class="source-table-filter__empty">
            Aucune option.
          </p>
          <div
            v-for="option in filteredOptions"
            :key="option.value"
            class="source-table-filter__option"
          >
            <SourceCheckbox
              size="small"
              :label="option.label"
              :model-value="isChecked(option.value)"
              @update:model-value="(checked) => toggleValue(option.value, checked)"
            />
            <span
              v-if="option.count != null"
              class="source-table-filter__count"
            >{{ formatCount(option.count) }}</span>
          </div>
        </div>

        <div class="source-table-filter__footer">
          <button
            type="button"
            class="source-table-filter__footer-btn"
            @click="onReset"
          >
            Réinitialiser
          </button>
          <button
            type="button"
            class="source-table-filter__footer-btn"
            @click="onApply"
          >
            Appliquer
          </button>
        </div>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/colors" as c;
@use "~/assets/scss/partials/elpdl/spacing" as *;

.source-table-filter__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: c.$enedis-neutral-500;
  cursor: pointer;

  .material-symbols-rounded {
    font-size: 1.125rem;
    font-variation-settings: "FILL" 0, "wght" 500, "GRAD" 0, "opsz" 20;
  }

  &:hover {
    background: var(--color-inverse-hover);
    color: c.$enedis-neutral-700;
  }

  &.is-active {
    color: c.$enedis-primary-500;
  }

  &:focus-visible {
    outline: 2px solid c.$enedis-primary-500;
    outline-offset: 2px;
  }
}
</style>

<!-- Non-scoped : PopoverContent est porté hors du composant (Reka Portal) sans data-v. -->
<style lang="scss">
@use "~/assets/scss/partials/elpdl/colors" as c;
@use "~/assets/scss/partials/elpdl/spacing" as *;
@use "~/assets/scss/partials/elpdl/elevation" as elev;

.source-table-filter__menu {
  display: flex;
  flex-direction: column;
  width: min(17.5rem, calc(100vw - 2 * #{$spacing-m}));
  padding: $spacing-m $spacing-m 0;
  background: c.$enedis-neutral-10;
  border: 1px solid c.$enedis-neutral-300;
  border-radius: 4px;
  box-sizing: border-box;
  @include elev.elevation-100;
}

.source-table-filter__search {
  margin-bottom: $spacing-l;
}

.source-table-filter__search-input {
  width: 100%;

  .input-container {
    gap: $spacing-2xs;
    width: 100%;
  }

  input {
    min-width: 0;
    width: 100%;
  }

  /* Masque le clear natif trop visible sur search. */
  input::-webkit-search-cancel-button {
    appearance: none;
  }
}

.source-table-filter__options {
  display: flex;
  flex-direction: column;
  gap: $spacing-l;
  max-height: 12.5rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0 $spacing-3xs $spacing-m 0;
  margin: 0;
  scrollbar-width: thin;
  scrollbar-color: c.$enedis-neutral-60 transparent;

  &::-webkit-scrollbar {
    width: $spacing-2xs;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background: c.$enedis-neutral-60;
  }
}

.source-table-filter__empty {
  margin: 0;
  padding: $spacing-s 0;
  color: c.$enedis-neutral-300;
  font-size: 0.875rem;
  font-family: Public Sans, sans-serif;
}

.source-table-filter__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $spacing-s;
  min-width: 0;
}

.source-table-filter__count {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 1.75rem;
  height: 1.75rem;
  padding: 0 $spacing-3xs;
  border: 1.5px solid c.$enedis-primary-500;
  border-radius: 999px;
  background: c.$enedis-neutral-10;
  color: c.$enedis-neutral-800;
  font-size: 0.75rem;
  font-weight: 500;
  font-family: Public Sans, sans-serif;
  line-height: 1;
}

.source-table-filter__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $spacing-s;
  padding: $spacing-m 0;
  border-top: 1px solid c.$enedis-neutral-60;
}

.source-table-filter__footer-btn {
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  font-family: Public Sans, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  color: c.$enedis-primary-500;
  padding: $spacing-3xs 0;
  border-radius: 2px;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid c.$enedis-primary-500;
    outline-offset: 2px;
  }
}
</style>
